'use client';

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@kit/ui/card';
import { Checkbox } from '@kit/ui/checkbox';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Separator } from '@kit/ui/separator';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

import { APPLICATION_FIELD_TYPES } from '../../constants/audition.constants';
import type {
  ApplicationFieldConfig,
  ApplicationStructure,
  FieldCondition,
  FieldOption,
} from '../../types/audition.types';

interface FieldConfigEditorProps {
  field: ApplicationFieldConfig;
  structure: ApplicationStructure;
  onChange: (updatedField: ApplicationFieldConfig) => void;
}

/**
 * Editor for configuring a single form field
 */
export function FieldConfigEditor({
  field,
  structure,
  onChange,
}: FieldConfigEditorProps) {
  const { t } = useTranslation();
  const fieldMeta = APPLICATION_FIELD_TYPES.find((f) => f.value === field.type);
  const hasOptions = fieldMeta?.hasOptions || false;

  // Get other fields for condition references (exclude current field)
  const otherFields = structure.fields.filter((f) => f.key !== field.key);

  const handleChange = useCallback(
    <K extends keyof ApplicationFieldConfig>(
      key: K,
      value: ApplicationFieldConfig[K],
    ) => {
      onChange({ ...field, [key]: value });
    },
    [field, onChange],
  );

  const handleValidationChange = useCallback(
    <K extends keyof NonNullable<ApplicationFieldConfig['validation']>>(
      key: K,
      value: NonNullable<ApplicationFieldConfig['validation']>[K],
    ) => {
      onChange({
        ...field,
        validation: { ...field.validation, [key]: value },
      });
    },
    [field, onChange],
  );

  const handleAddOption = useCallback(() => {
    const options = field.options || [];
    const newValue = `option${options.length + 1}`;
    onChange({
      ...field,
      options: [
        ...options,
        { value: newValue, label: `Option ${options.length + 1}` },
      ],
    });
  }, [field, onChange]);

  const handleUpdateOption = useCallback(
    (index: number, updates: Partial<FieldOption>) => {
      const options = [...(field.options || [])];
      const currentOption = options[index];
      if (currentOption) {
        options[index] = { ...currentOption, ...updates };
        onChange({ ...field, options });
      }
    },
    [field, onChange],
  );

  const handleRemoveOption = useCallback(
    (index: number) => {
      const options = [...(field.options || [])];
      options.splice(index, 1);
      onChange({ ...field, options });
    },
    [field, onChange],
  );

  const handleAddCondition = useCallback(() => {
    const conditions = field.conditions || [];
    const firstOtherField = otherFields[0];
    if (!firstOtherField) return;

    onChange({
      ...field,
      conditions: [
        ...conditions,
        { fieldKey: firstOtherField.key, operator: 'isNotEmpty' },
      ],
    });
  }, [field, otherFields, onChange]);

  const handleUpdateCondition = useCallback(
    (index: number, updates: Partial<FieldCondition>) => {
      const conditions = [...(field.conditions || [])];
      const currentCondition = conditions[index];
      if (currentCondition) {
        conditions[index] = { ...currentCondition, ...updates };
        onChange({ ...field, conditions });
      }
    },
    [field, onChange],
  );

  const handleRemoveCondition = useCallback(
    (index: number) => {
      const conditions = [...(field.conditions || [])];
      conditions.splice(index, 1);
      onChange({ ...field, conditions });
    },
    [field, onChange],
  );

  const isProfileSource = field.source === 'profile';
  // Controls are read-only for profile-bound or system-enforced (locked) fields.
  const isProfileField = isProfileSource || field.locked === true;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          <Trans i18nKey="auditions:formBuilder.fieldConfig.title" />
        </CardTitle>
        <CardDescription>
          <Trans
            i18nKey="auditions:formBuilder.fieldConfig.configureField"
            values={{ field: fieldMeta?.label || field.type }}
          />
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isProfileField && (
          <p className="bg-muted text-muted-foreground rounded-md px-3 py-2 text-xs">
            {isProfileSource ? (
              <Trans i18nKey="auditions:formBuilder.fieldConfig.profileFieldNote" />
            ) : (
              <Trans i18nKey="auditions:formBuilder.fieldConfig.requiredCompetitionNote" />
            )}{' '}
            <Trans i18nKey="auditions:formBuilder.fieldConfig.managedCentrallyNote" />
          </p>
        )}
        {/* Basic Settings */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="field-key">
              <Trans i18nKey="auditions:formBuilder.fieldConfig.fieldKey" />
            </Label>
            <Input
              id="field-key"
              value={field.key}
              onChange={(e) => handleChange('key', e.target.value)}
              placeholder="fieldKey"
              disabled={isProfileField}
              className="mt-1.5 font-mono text-sm"
            />
            <p className="text-muted-foreground mt-1 text-xs">
              <Trans i18nKey="auditions:formBuilder.fieldConfig.fieldKeyHint" />
            </p>
          </div>

          <div>
            <Label htmlFor="field-label">
              <Trans i18nKey="auditions:formBuilder.fieldConfig.label" />
            </Label>
            <Input
              id="field-label"
              value={field.label}
              onChange={(e) => handleChange('label', e.target.value)}
              placeholder={t(
                'auditions:formBuilder.fieldConfig.labelPlaceholder',
              )}
              className="mt-1.5"
            />
          </div>

          {field.type !== 'heading' && field.type !== 'paragraph' && (
            <>
              <div>
                <Label htmlFor="field-placeholder">
                  <Trans i18nKey="auditions:formBuilder.fieldConfig.placeholder" />
                </Label>
                <Input
                  id="field-placeholder"
                  value={field.placeholder || ''}
                  onChange={(e) => handleChange('placeholder', e.target.value)}
                  placeholder={t(
                    'auditions:formBuilder.fieldConfig.placeholderPlaceholder',
                  )}
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="field-description">
                  <Trans i18nKey="auditions:formBuilder.fieldConfig.helpText" />
                </Label>
                <Textarea
                  id="field-description"
                  value={field.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder={t(
                    'auditions:formBuilder.fieldConfig.helpTextPlaceholder',
                  )}
                  rows={2}
                  className="mt-1.5"
                />
              </div>
            </>
          )}

          <div>
            <Label htmlFor="field-width">
              <Trans i18nKey="auditions:formBuilder.fieldConfig.fieldWidth" />
            </Label>
            <Select
              value={field.width || 'full'}
              onValueChange={(value) =>
                handleChange('width', value as 'full' | 'half' | 'third')
              }
            >
              <SelectTrigger id="field-width" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full">
                  <Trans i18nKey="auditions:formBuilder.fieldConfig.widthFull" />
                </SelectItem>
                <SelectItem value="half">
                  <Trans i18nKey="auditions:formBuilder.fieldConfig.widthHalf" />
                </SelectItem>
                <SelectItem value="third">
                  <Trans i18nKey="auditions:formBuilder.fieldConfig.widthThird" />
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Options for selection fields */}
        {hasOptions && (
          <>
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>
                  <Trans i18nKey="auditions:formBuilder.fieldConfig.options" />
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isProfileField}
                  onClick={handleAddOption}
                >
                  <Plus className="mr-2 h-3 w-3" />
                  <Trans i18nKey="auditions:formBuilder.fieldConfig.addOption" />
                </Button>
              </div>
              <div className="space-y-2">
                {(field.options || []).map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={option.value}
                      onChange={(e) =>
                        handleUpdateOption(index, { value: e.target.value })
                      }
                      placeholder={t(
                        'auditions:formBuilder.fieldConfig.valuePlaceholder',
                      )}
                      disabled={isProfileField}
                      className="flex-1 font-mono text-sm"
                    />
                    <Input
                      value={option.label}
                      onChange={(e) =>
                        handleUpdateOption(index, { label: e.target.value })
                      }
                      placeholder={t(
                        'auditions:formBuilder.fieldConfig.labelOptionPlaceholder',
                      )}
                      disabled={isProfileField}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={isProfileField}
                      className="text-muted-foreground hover:text-destructive h-9 w-9 flex-shrink-0"
                      onClick={() => handleRemoveOption(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {(!field.options || field.options.length === 0) && (
                  <p className="text-muted-foreground py-2 text-center text-sm">
                    <Trans i18nKey="auditions:formBuilder.fieldConfig.noOptions" />
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Validation Settings */}
        {field.type !== 'heading' && field.type !== 'paragraph' && (
          <>
            <Separator />
            <div className="space-y-4">
              <Label>
                <Trans i18nKey="auditions:formBuilder.fieldConfig.validation" />
              </Label>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="field-required"
                  checked={field.validation?.required || false}
                  disabled={isProfileField}
                  onCheckedChange={(checked) =>
                    handleValidationChange('required', checked as boolean)
                  }
                />
                <Label htmlFor="field-required" className="font-normal">
                  <Trans i18nKey="auditions:formBuilder.fieldConfig.requiredField" />
                </Label>
              </div>

              {(field.type === 'text' || field.type === 'textarea') && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="min-length">
                      <Trans i18nKey="auditions:formBuilder.fieldConfig.minLength" />
                    </Label>
                    <Input
                      id="min-length"
                      type="number"
                      min={0}
                      value={field.validation?.minLength ?? ''}
                      onChange={(e) =>
                        handleValidationChange(
                          'minLength',
                          e.target.value ? parseInt(e.target.value) : undefined,
                        )
                      }
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="max-length">
                      <Trans i18nKey="auditions:formBuilder.fieldConfig.maxLength" />
                    </Label>
                    <Input
                      id="max-length"
                      type="number"
                      min={1}
                      value={field.validation?.maxLength ?? ''}
                      onChange={(e) =>
                        handleValidationChange(
                          'maxLength',
                          e.target.value ? parseInt(e.target.value) : undefined,
                        )
                      }
                      className="mt-1.5"
                    />
                  </div>
                </div>
              )}

              {field.type === 'number' && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="min-value">
                      <Trans i18nKey="auditions:formBuilder.fieldConfig.minValue" />
                    </Label>
                    <Input
                      id="min-value"
                      type="number"
                      value={field.validation?.min ?? ''}
                      onChange={(e) =>
                        handleValidationChange(
                          'min',
                          e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        )
                      }
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="max-value">
                      <Trans i18nKey="auditions:formBuilder.fieldConfig.maxValue" />
                    </Label>
                    <Input
                      id="max-value"
                      type="number"
                      value={field.validation?.max ?? ''}
                      onChange={(e) =>
                        handleValidationChange(
                          'max',
                          e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        )
                      }
                      className="mt-1.5"
                    />
                  </div>
                </div>
              )}

              {(field.type === 'file' || field.type === 'video') && (
                <div>
                  <Label htmlFor="file-types">
                    <Trans i18nKey="auditions:formBuilder.fieldConfig.allowedFileTypes" />
                  </Label>
                  <Input
                    id="file-types"
                    value={field.validation?.fileTypes?.join(', ') || ''}
                    onChange={(e) =>
                      handleValidationChange(
                        'fileTypes',
                        e.target.value
                          ? e.target.value.split(',').map((mime) => mime.trim())
                          : undefined,
                      )
                    }
                    placeholder="image/jpeg, image/png, application/pdf"
                    className="mt-1.5"
                  />
                  <p className="text-muted-foreground mt-1 text-xs">
                    <Trans i18nKey="auditions:formBuilder.fieldConfig.mimeTypesHint" />
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Conditional Visibility */}
        {field.type !== 'heading' &&
          field.type !== 'paragraph' &&
          otherFields.length > 0 && (
            <>
              <Separator />
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>
                      <Trans i18nKey="auditions:formBuilder.fieldConfig.conditionalVisibility" />
                    </Label>
                    <p className="text-muted-foreground text-xs">
                      <Trans i18nKey="auditions:formBuilder.fieldConfig.conditionalVisibilityHint" />
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddCondition}
                  >
                    <Plus className="mr-2 h-3 w-3" />
                    <Trans i18nKey="auditions:formBuilder.fieldConfig.addCondition" />
                  </Button>
                </div>

                {(field.conditions || []).length > 0 && (
                  <div className="space-y-3">
                    {field.conditions?.length &&
                      field.conditions.length > 1 && (
                        <div className="flex items-center gap-2">
                          <Label className="text-sm">
                            <Trans i18nKey="auditions:formBuilder.fieldConfig.match" />
                          </Label>
                          <Select
                            value={field.conditionLogic || 'and'}
                            onValueChange={(value) =>
                              handleChange(
                                'conditionLogic',
                                value as 'and' | 'or',
                              )
                            }
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="and">
                                <Trans i18nKey="auditions:formBuilder.fieldConfig.matchAll" />
                              </SelectItem>
                              <SelectItem value="or">
                                <Trans i18nKey="auditions:formBuilder.fieldConfig.matchAny" />
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                    {field.conditions?.map((condition, index) => (
                      <div
                        key={index}
                        className="flex flex-wrap items-center gap-2 rounded-lg border p-3"
                      >
                        <Select
                          value={condition.fieldKey}
                          onValueChange={(value) =>
                            handleUpdateCondition(index, { fieldKey: value })
                          }
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue
                              placeholder={t(
                                'auditions:formBuilder.fieldConfig.selectField',
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {otherFields.map((f) => (
                              <SelectItem key={f.key} value={f.key}>
                                {f.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={condition.operator}
                          onValueChange={(value) =>
                            handleUpdateCondition(index, {
                              operator: value as FieldCondition['operator'],
                            })
                          }
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="equals">
                              <Trans i18nKey="auditions:formBuilder.fieldConfig.operators.equals" />
                            </SelectItem>
                            <SelectItem value="notEquals">
                              <Trans i18nKey="auditions:formBuilder.fieldConfig.operators.notEquals" />
                            </SelectItem>
                            <SelectItem value="contains">
                              <Trans i18nKey="auditions:formBuilder.fieldConfig.operators.contains" />
                            </SelectItem>
                            <SelectItem value="notContains">
                              <Trans i18nKey="auditions:formBuilder.fieldConfig.operators.notContains" />
                            </SelectItem>
                            <SelectItem value="isEmpty">
                              <Trans i18nKey="auditions:formBuilder.fieldConfig.operators.isEmpty" />
                            </SelectItem>
                            <SelectItem value="isNotEmpty">
                              <Trans i18nKey="auditions:formBuilder.fieldConfig.operators.isNotEmpty" />
                            </SelectItem>
                            <SelectItem value="greaterThan">
                              <Trans i18nKey="auditions:formBuilder.fieldConfig.operators.greaterThan" />
                            </SelectItem>
                            <SelectItem value="lessThan">
                              <Trans i18nKey="auditions:formBuilder.fieldConfig.operators.lessThan" />
                            </SelectItem>
                            <SelectItem value="greaterOrEqual">
                              <Trans i18nKey="auditions:formBuilder.fieldConfig.operators.greaterOrEqual" />
                            </SelectItem>
                            <SelectItem value="lessOrEqual">
                              <Trans i18nKey="auditions:formBuilder.fieldConfig.operators.lessOrEqual" />
                            </SelectItem>
                            <SelectItem value="onOrBefore">
                              <Trans i18nKey="auditions:formBuilder.fieldConfig.operators.onOrBefore" />
                            </SelectItem>
                            <SelectItem value="onOrAfter">
                              <Trans i18nKey="auditions:formBuilder.fieldConfig.operators.onOrAfter" />
                            </SelectItem>
                          </SelectContent>
                        </Select>

                        {!['isEmpty', 'isNotEmpty'].includes(
                          condition.operator,
                        ) && (
                          <Input
                            type={
                              ['onOrBefore', 'onOrAfter'].includes(
                                condition.operator,
                              )
                                ? 'date'
                                : 'text'
                            }
                            value={String(condition.value || '')}
                            onChange={(e) =>
                              handleUpdateCondition(index, {
                                value: e.target.value,
                              })
                            }
                            placeholder={t(
                              'auditions:formBuilder.fieldConfig.valuePlaceholder',
                            )}
                            className="w-32"
                          />
                        )}

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive h-9 w-9"
                          onClick={() => handleRemoveCondition(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

        {/* Admin-only toggle */}
        {field.type !== 'heading' && field.type !== 'paragraph' && (
          <>
            <Separator />
            <div className="flex items-center space-x-2">
              <Checkbox
                id="field-management"
                checked={field.isManagementField || false}
                onCheckedChange={(checked) =>
                  handleChange('isManagementField', checked as boolean)
                }
              />
              <div>
                <Label htmlFor="field-management" className="font-normal">
                  <Trans i18nKey="auditions:formBuilder.fieldConfig.adminOnly" />
                </Label>
                <p className="text-muted-foreground text-xs">
                  <Trans i18nKey="auditions:formBuilder.fieldConfig.adminOnlyHint" />
                </p>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
